import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Column } from "../../components/Table/types";
import { useHcpLandscapeState } from "../../data/hcpLandscape/hooks";
import { HcpLandscapeRow } from "../../generate";
import useGetRole, { FRM_ROLE } from "../../hooks/useGetRole";
import useHasHcpAccessAsFrm from "../../hooks/useHasHcpAccessAsFrm";
import { useHcpLandscapeColumnVisibility } from "./useHCPLandscapeColumnVisibility";

const hcpText = "hcpText" as const;
const hcpLink = "hcpLink" as const;

const useTableColumns = (client: string) => {
  const { t, i18n } = useTranslation();
  const { client: selectedClient } = useParams() as { client: string };
  const { data: role } = useGetRole(selectedClient);
  const isFrm = String(role) === FRM_ROLE;
  const {
    filters: { timePeriod, benificiaryType, indication },
  } = useHcpLandscapeState();

  const hasHcpAccessAsFrm = useHasHcpAccessAsFrm(selectedClient, isFrm);
  const { visibleColumns } = useHcpLandscapeColumnVisibility(client, indication, isFrm, String(role));

  const client_indicationkey =
    selectedClient === "BRAFTOVI" ? selectedClient + "_" + indication?.replaceAll(" ", "") : selectedClient;

  const allColumns: Column<keyof HcpLandscapeRow | "full_name">[] = [
    {
      label: `hcpLandscape.hcps.${client}`,
      align: "left",
      dataKey: "full_name",
      component: hasHcpAccessAsFrm ? hcpText : hcpLink,
    },
    {
      label: i18n.exists(`hcpLandscape.favorableAccess.${client_indicationkey}`)
        ? `hcpLandscape.favorableAccess.${client_indicationkey}`
        : `hcpLandscape.favorableAccess.default`,
      align: "right",
      dataKey: "fav_pct",
      width: 180,
      format: {
        suffix: "%",
        decimalScale: 1,
        fixedDecimalScale: true,
      },
    },
    {
      label: i18n.exists(`hcpLandscape.changeInFa.${client}`)
        ? `hcpLandscape.changeInFa.${client}`
        : `hcpLandscape.changeInFa.default`,
      translationParams: { channel: benificiaryType || "" },
      align: "right",
      dataKey: "delta_fa",
      width: 160,
      format: {
        suffix: "%",
        decimalScale: 1,
        fixedDecimalScale: true,
      },
    },
    {
      label: i18n.exists(`hcpLandscape.overallAccess.${client}`)
        ? `hcpLandscape.overallAccess.${client}`
        : `hcpLandscape.overallAccess.default`,
      align: "right",
      dataKey: "acc_pct",
      width: 170,
      format: {
        suffix: "%",
        decimalScale: 1,
        fixedDecimalScale: true,
      },
    },
    {
      label: timePeriod
        ? i18n.exists(`hcpLandscape.marketTrx.${timePeriod}.${client}`)
          ? `hcpLandscape.marketTrx.${timePeriod}.${client}`
          : `hcpLandscape.marketTrx.${timePeriod}.default`
        : "Loading...",
      align: "right",
      dataKey: "mkt_trx",
      width: 130,
      format: { thousandSeparator: true },
    },
    {
      label: timePeriod
        ? i18n.exists(`hcpLandscape.productTrx.${timePeriod}.${client_indicationkey}`)
          ? `hcpLandscape.productTrx.${timePeriod}.${client_indicationkey}`
          : `hcpLandscape.productTrx.${timePeriod}.default`
        : "Loading...",
      translationParams: { product: client },
      align: "right",
      dataKey: "prod_trx",
      width: 150,
      format: { thousandSeparator: true },
    },
    {
      label: timePeriod ? `hcpLandscape.productNrx.${timePeriod}.${client_indicationkey}` : "Loading...",
      translationParams: { product: client },
      align: "right",
      dataKey: "prod_nrx",
      width: 150,
      format: { thousandSeparator: true },
    },
    {
      label: timePeriod
        ? t(`hcpLandscape.compProdTrx.${timePeriod}.${client}`, { channel: benificiaryType })
        : "Loading...",
      translationParams: { product: client },
      align: "right",
      dataKey: "comp_prod_trx",
      width: 150,
      format: (() => {
        // Define format types for type safety
        type FormatConfig = {
          thousandSeparator?: boolean;
          suffix?: string;
          prefix?: string;
          decimalScale?: number;
          fixedDecimalScale?: boolean;
        };

        // Default format for most clients
        const defaultFormat: FormatConfig = {
          thousandSeparator: true,
        };

        // Client-specific formats with index signature for string keys
        const clientFormats: Record<string, FormatConfig> = {
          // Special format for PAXLOVID and PAXLOVID_PUCC (column is % BoB)
          PAXLOVID: {
            suffix: "%",
            decimalScale: 1,
            fixedDecimalScale: true,
          },
          PAXLOVID_PUCC: {
            suffix: "%",
            decimalScale: 1,
            fixedDecimalScale: true,
          },
        };

        // Return the specific format if client exists in clientFormats, otherwise return default
        return Object.prototype.hasOwnProperty.call(clientFormats, client) ? clientFormats[client] : defaultFormat;
      })(),
    },
    {
      label: timePeriod
        ? i18n.exists(`hcpLandscape.productShare.${timePeriod}.${client}`)
          ? `hcpLandscape.productShare.${timePeriod}.${client}`
          : `hcpLandscape.productShare.${timePeriod}.default`
        : "Loading...",
      translationParams: { product: client },
      align: "right",
      dataKey: "prod_share",
      width: 150,
      format: {
        suffix: "%",
        decimalScale: 1,
        fixedDecimalScale: true,
      },
    },
  ];

  // Filter and sort columns according to visibleColumns
  const sortedColumns = visibleColumns
    .map((key) => allColumns.find((col) => col.dataKey === key))
    .filter(Boolean) as Column<keyof HcpLandscapeRow | "full_name">[];

  return { columns: sortedColumns };
};

export default useTableColumns;
